import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  BookOpen, 
  Users, 
  GraduationCap, 
  Handshake, 
  FolderOpen, 
  UserCheck,
  BarChart3,
  LogOut,
  TrendingUp,
  Euro,
  Download,
  Key,
  Lock,
  X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard({ admin, onLogout }) {
  const [stats, setStats] = useState({
    total_trainings: 0,
    published_trainings: 0,
    total_learners: 0,
    total_trainers: 0,
    total_partnerships: 0,
    total_registrations: 0,
    confirmed_registrations: 0,
    pending_registrations: 0,
    total_revenue: 0
  });
  const [loading, setLoading] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/statistics/overview`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadCredentialsPdf = async () => {
    try {
      const response = await axios.get(`${API}/documents/mes-acces`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'insitumed_mes_acces.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading credentials:', error);
      alert('Erreur lors du téléchargement');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordError('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (passwordData.new_password.length < 8) {
      setPasswordError('Le nouveau mot de passe doit contenir au moins 8 caractères');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(`${API}/admin/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      setPasswordSuccess('✅ Mot de passe modifié avec succès !');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      setTimeout(() => {
        setShowPasswordModal(false);
        setPasswordSuccess('');
      }, 2000);
    } catch (error) {
      setPasswordError(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setChangingPassword(false);
    }
  };

  const menuItems = [
    {
      title: 'Formations',
      description: 'Gérer les formations et le calendrier',
      icon: BookOpen,
      path: '/admin/formations',
      color: 'from-violet-500 to-purple-500',
      testId: 'nav-formations'
    },
    {
      title: 'Apprenants',
      description: 'Suivi des apprenants et présences',
      icon: Users,
      path: '/admin/apprenants',
      color: 'from-purple-500 to-fuchsia-500',
      testId: 'nav-apprenants'
    },
    {
      title: 'Formateurs',
      description: 'Gestion des formateurs',
      icon: GraduationCap,
      path: '/admin/formateurs',
      color: 'from-fuchsia-500 to-pink-500',
      testId: 'nav-formateurs'
    },
    {
      title: 'Partenariats',
      description: 'Suivi des collaborations',
      icon: Handshake,
      path: '/admin/partenariats',
      color: 'from-pink-500 to-rose-500',
      testId: 'nav-partenariats'
    },
    {
      title: 'Ressources',
      description: 'Contenus pédagogiques',
      icon: FolderOpen,
      path: '/admin/ressources',
      color: 'from-violet-500 to-indigo-500',
      testId: 'nav-ressources'
    },
    {
      title: 'Inscriptions',
      description: 'Gestion des inscriptions',
      icon: UserCheck,
      path: '/admin/inscriptions',
      color: 'from-indigo-500 to-blue-500',
      testId: 'nav-inscriptions'
    },
    {
      title: 'Propositions',
      description: 'Propositions des partenaires',
      icon: Handshake,
      path: '/admin/propositions',
      color: 'from-orange-500 to-amber-500',
      testId: 'nav-propositions'
    },
    {
      title: 'Statistiques',
      description: 'Analytics et graphiques',
      icon: TrendingUp,
      path: '/admin/statistiques',
      color: 'from-teal-500 to-cyan-500',
      testId: 'nav-statistiques'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50">
      {/* Header */}
      <header className="bg-white shadow-md border-b border-violet-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold gradient-text" data-testid="dashboard-title">
                Insitumed
              </h1>
              <p className="text-sm text-gray-600 mt-1">Centre de Formation</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-700" data-testid="admin-name">
                  {admin?.full_name}
                </p>
                <p className="text-xs text-gray-500">{admin?.email}</p>
              </div>
              <button
                onClick={() => setShowPasswordModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-violet-50 text-violet-600 rounded-lg hover:bg-violet-100 transition-colors"
                data-testid="change-password-button"
              >
                <Lock size={18} />
                <span className="text-sm font-medium hidden sm:inline">Mot de passe</span>
              </button>
              <button
                onClick={onLogout}
                className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                data-testid="logout-button"
              >
                <LogOut size={18} />
                <span className="text-sm font-medium hidden sm:inline">Déconnexion</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <Lock className="text-violet-500" />
                Changer mon mot de passe
              </h3>
              <button 
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
                  setPasswordError('');
                  setPasswordSuccess('');
                }} 
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            {passwordError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4" data-testid="password-error">
                {passwordError}
              </div>
            )}

            {passwordSuccess && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4" data-testid="password-success">
                {passwordSuccess}
              </div>
            )}

            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mot de passe actuel *
                </label>
                <input
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                  className="input-violet"
                  required
                  data-testid="current-password-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nouveau mot de passe * <span className="text-xs text-gray-500">(min. 8 caractères)</span>
                </label>
                <input
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                  className="input-violet"
                  minLength={8}
                  required
                  data-testid="new-password-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirmer le nouveau mot de passe *
                </label>
                <input
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                  className="input-violet"
                  minLength={8}
                  required
                  data-testid="confirm-password-input"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={changingPassword}
                  className="btn-violet flex-1"
                  data-testid="submit-password-change"
                >
                  {changingPassword ? 'Modification...' : 'Modifier'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
                    setPasswordError('');
                    setPasswordSuccess('');
                  }}
                  className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Welcome Section */}
        <div className="mb-12">
          <h2 className="text-4xl font-bold text-gray-800 mb-3">
            Bienvenue ! 👋
          </h2>
          <p className="text-lg text-gray-600">
            Tableau de bord administrateur - Gérez votre centre de formation
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
          <div className="bg-white rounded-xl p-6 shadow-md border border-violet-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Formations</p>
                <p className="text-3xl font-bold text-violet-600" data-testid="stat-formations">
                  {loading ? '...' : stats.total_trainings}
                </p>
                <p className="text-xs text-gray-500 mt-1">{stats.published_trainings} publiées</p>
              </div>
              <div className="p-3 bg-violet-100 rounded-lg">
                <BookOpen className="text-violet-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-md border border-purple-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Apprenants</p>
                <p className="text-3xl font-bold text-purple-600" data-testid="stat-apprenants">
                  {loading ? '...' : stats.total_learners}
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <Users className="text-purple-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-md border border-fuchsia-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Inscriptions</p>
                <p className="text-3xl font-bold text-fuchsia-600" data-testid="stat-inscriptions">
                  {loading ? '...' : stats.total_registrations}
                </p>
                <p className="text-xs text-gray-500 mt-1">{stats.confirmed_registrations} confirmées</p>
              </div>
              <div className="p-3 bg-fuchsia-100 rounded-lg">
                <BarChart3 className="text-fuchsia-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-md border border-green-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Revenu total</p>
                <p className="text-3xl font-bold text-green-600" data-testid="stat-revenue">
                  {loading ? '...' : `${stats.total_revenue.toFixed(0)}€`}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <Euro className="text-green-600" size={24} />
              </div>
            </div>
          </div>
        </div>

        {/* Menu Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className="group"
                data-testid={item.testId}
              >
                <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <div className={`inline-flex p-3 rounded-lg bg-gradient-to-r ${item.color} mb-4`}>
                    <Icon className="text-white" size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-violet-600 transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {item.description}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Quick Link to Public Portal */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-gradient-to-r from-violet-500 to-purple-600 rounded-xl p-6 text-white">
            <h3 className="text-xl font-bold mb-2">🌍 Portail Public</h3>
            <p className="text-violet-100 text-sm mb-4">
              Partagez ce lien avec vos futurs apprenants
            </p>
            <a
              href="/catalogue"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-4 py-2 bg-white text-violet-600 rounded-lg font-semibold hover:bg-violet-50 transition-colors text-sm"
              data-testid="public-portal-link"
            >
              Voir le catalogue →
            </a>
          </div>

          <div className="bg-gradient-to-r from-fuchsia-500 to-pink-600 rounded-xl p-6 text-white">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                  <Key size={20} /> Mes Accès
                </h3>
                <p className="text-fuchsia-100 text-sm mb-4">
                  Téléchargez vos identifiants et URLs en PDF
                </p>
              </div>
            </div>
            <button
              onClick={downloadCredentialsPdf}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white text-fuchsia-600 rounded-lg font-semibold hover:bg-fuchsia-50 transition-colors text-sm"
              data-testid="download-credentials-btn"
            >
              <Download size={16} />
              Télécharger PDF
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
